import type {
  UniqueIdentifier
} from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { CSSProperties, PropsWithChildren, ReactNode } from 'react';

import styled from 'styled-components';

interface Props {
  id: UniqueIdentifier;
}

const GripIcon = () => (
  <svg viewBox="0 0 20 20" width="12" aria-hidden="true">
    <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z"></path>
  </svg>
);

type RowProps = {
  // The grip element: an activator <button> for a real sortable row, an inert <span> for the
  // overlay clone, which must not be focusable or carry drag listeners.
  grip: ReactNode;
  children: ReactNode;
};

const Row = ({ grip, children }: RowProps) => (
  <div className="SortableItem">
    {grip}
    <div className="SortableItemContent">{children}</div>
  </div>
);

function SortableItem({ children, id }: PropsWithChildren<Props>) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition
  } = useSortable({ id });

  const style: CSSProperties = {
    opacity: isDragging ? 0.4 : undefined,
    transform: CSS.Translate.toString(transform),
    transition
  };

  return (
    // The sortable node and its transform belong to the outermost element: keeping them on an inner
    // child left the layout slot behind while the content slid, so the drop preview never matched
    // where the row would actually land.
    <WrapStyled ref={setNodeRef} style={style}>
      <Row
        grip={
          // Drag activates from the grip alone. A row-wide activator would force `touch-action:
          // none` across the whole row, and the list would stop scrolling by swipe.
          <button
            type="button"
            className="DragHandle"
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            aria-label="Drag to reorder"
          >
            <GripIcon />
          </button>
        }
      >
        {children}
      </Row>
    </WrapStyled>
  );
}

export default SortableItem;

// Presentational twin for DragOverlay. It must not call useSortable: registering the same id twice
// makes the overlay's fixed-position node the measured node for that id, which double-applies the
// drag transform and drops the real row out of the droppable registry when the overlay unmounts.
export function StaticItem({ children }: PropsWithChildren) {
  return (
    <WrapStyled>
      <Row grip={<span className="DragHandle" aria-hidden="true"><GripIcon /></span>}>
        {children}
      </Row>
    </WrapStyled>
  );
}

const WrapStyled = styled.div`
.SortableItem {
  display: flex;
  align-items: center;
  padding: 18px 20px;
  box-shadow: 0 0 0 calc(1px / var(--scale-x, 1)) rgba(63, 63, 68, 0.05),
    0 1px calc(3px / var(--scale-x, 1)) 0 rgba(34, 33, 81, 0.15);
  border-radius: calc(4px / var(--scale-x, 1));
  box-sizing: border-box;
  list-style: none;
  color: #333;
  font-weight: 400;
  font-size: 1rem;
  font-family: sans-serif;
  /* Android raises its text-selection UI on long-press over selectable content, and that UI then
     swallows the pointer stream mid-drag. The row keeps touch-action: auto so swiping it scrolls. */
  -webkit-user-select: none;
  user-select: none;
}

.SortableItemContent {
  flex: 1;
  min-width: 0;
}

.DragHandle {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  /* Stretch to the row's own height instead of setting one: a min-height here would have made
     every row ~12px taller than before, costing a visible row in the 70vh list. */
  align-self: stretch;
  min-width: 44px;
  margin-right: 0.5rem;
  border-radius: 5px;
  border: none;
  outline: none;
  appearance: none;
  background-color: transparent;
  -webkit-tap-highlight-color: transparent;
  /* Required on the drag activator: without it Android hands the touch to the compositor for
     scrolling, and once native scroll starts touchmove is no longer cancelable, so dnd-kit can
     never claim the gesture. */
  touch-action: none;
  -webkit-touch-callout: none;
}

.DragHandle:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.DragHandle:focus-visible {
  box-shadow: 0 0px 0px 2px #4c9ffe;
}

.DragHandle svg {
  flex: 0 0 auto;
  margin: auto;
  overflow: visible;
  fill: #919eab;
}

`;
